// "Add details" (T05), opened from the report. Entirely optional: anything left blank stays
// unknown and the report says which checks that affected. Sites default to England
// and hospitality/retail (D23, D24) without the user having to say so.
import { useState } from 'preact/hooks';
import {
  NO_DETAILS,
  type Details,
  type EmployeeDetails,
  type SiteDetails,
} from '../../import/details';
import type { CanonicalRota, Sector } from '../../model/types';

interface Props {
  rota: CanonicalRota;
  /** Answers given earlier in this session, so reopening the step doesn't lose them. */
  initial?: Details;
  /** Called with the answers; the worker applies them and re-runs the checks. */
  onDone(details: Details): void;
  onBack(): void;
}

const SECTORS: [Sector, string][] = [
  ['hotel-pub-restaurant-bar', 'Pub, bar, restaurant or hotel'],
  ['catering', 'Catering'],
  ['retail', 'Shop or retail'],
  ['bakery', 'Bakery'],
  ['other', 'Something else'],
];

type YesNo = '' | 'yes' | 'no';
const toYesNo = (v: boolean | undefined): YesNo => (v === undefined ? '' : v ? 'yes' : 'no');
const fromYesNo = (v: string): boolean | undefined => (v === '' ? undefined : v === 'yes');

export function DetailsStep({ rota, initial, onDone, onBack }: Props) {
  const [details, setDetails] = useState<Details>(initial ?? NO_DETAILS);

  const setSite = (ref: string, patch: SiteDetails) =>
    setDetails((d) => ({ ...d, sites: { ...d.sites, [ref]: { ...d.sites[ref], ...patch } } }));
  const setEmployee = (ref: string, patch: EmployeeDetails) =>
    setDetails((d) => ({
      ...d,
      employees: { ...d.employees, [ref]: { ...d.employees[ref], ...patch } },
    }));

  return (
    <section aria-labelledby="details-heading">
      <h2 id="details-heading">Add details</h2>
      <p>
        Tell us anything your rota doesn't show, and we'll update the report. Leave anything you're
        not sure about as it is.
      </p>

      {rota.sites.length > 0 && (
        <fieldset>
          <legend>Your sites</legend>
          <div class="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Site</th>
                  <th scope="col">What kind of business?</th>
                </tr>
              </thead>
              <tbody>
                {rota.sites.map((site) => {
                  const answer = { ...site, ...details.sites[site.ref] };
                  return (
                    <tr key={site.ref}>
                      <th scope="row">{site.ref}</th>
                      <td>
                        <select
                          aria-label={`What kind of business is ${site.ref}?`}
                          value={answer.sector ?? ''}
                          onChange={(e) =>
                            setSite(site.ref, {
                              sector: (e.currentTarget.value || undefined) as Sector | undefined,
                            })
                          }
                        >
                          <option value="">Hospitality or retail (assumed)</option>
                          {SECTORS.map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </fieldset>
      )}

      <fieldset>
        <legend>Your staff</legend>
        <div class="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Date of birth</th>
                <th scope="col">Under 18?</th>
                <th scope="col">Opted out of the 48-hour week?</th>
                <th scope="col">Night worker?</th>
                <th scope="col">Manager who sets their own hours?</th>
              </tr>
            </thead>
            <tbody>
              {rota.employees.map((employee) => {
                const answer = { ...employee, ...details.employees[employee.ref] };
                const yesNo = (
                  key: 'isUnder18' | 'optedOut48h' | 'isNightWorker' | 'setsOwnHours',
                  label: string,
                ) => (
                  <select
                    aria-label={`${label} ${employee.displayName}`}
                    value={toYesNo(answer[key])}
                    onChange={(e) =>
                      setEmployee(employee.ref, { [key]: fromYesNo(e.currentTarget.value) })
                    }
                  >
                    <option value="">Don't know</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                );
                return (
                  <tr key={employee.ref}>
                    <th scope="row">{employee.displayName}</th>
                    <td>
                      <input
                        type="date"
                        aria-label={`Date of birth for ${employee.displayName}`}
                        value={answer.dateOfBirth ?? ''}
                        onChange={(e) =>
                          setEmployee(employee.ref, {
                            dateOfBirth: e.currentTarget.value || undefined,
                          })
                        }
                      />
                    </td>
                    <td>{yesNo('isUnder18', 'Under 18:')}</td>
                    <td>{yesNo('optedOut48h', '48-hour opt-out:')}</td>
                    <td>{yesNo('isNightWorker', 'Night worker:')}</td>
                    <td>{yesNo('setsOwnHours', 'Sets own hours:')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </fieldset>

      <details>
        <summary>Less common details</summary>
        {rota.sites.length > 0 && (
          <fieldset>
            <legend>Site agreements</legend>
            <div class="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Site</th>
                    <th scope="col">Agreed night period (if not 11pm–6am)</th>
                    <th scope="col">Weekly rest</th>
                  </tr>
                </thead>
                <tbody>
                  {rota.sites.map((site) => {
                    const answer = { ...site, ...details.sites[site.ref] };
                    const night = answer.agreedNightPeriod;
                    const setNight = (part: 'start' | 'end', value: string) => {
                      const next = {
                        start: night?.start ?? '',
                        end: night?.end ?? '',
                        [part]: value,
                      };
                      setSite(site.ref, {
                        agreedNightPeriod: next.start && next.end ? next : undefined,
                      });
                    };
                    return (
                      <tr key={site.ref}>
                        <th scope="row">{site.ref}</th>
                        <td>
                          <input
                            type="time"
                            aria-label={`Night period start at ${site.ref}`}
                            value={night?.start ?? ''}
                            onChange={(e) => setNight('start', e.currentTarget.value)}
                          />{' '}
                          to{' '}
                          <input
                            type="time"
                            aria-label={`Night period end at ${site.ref}`}
                            value={night?.end ?? ''}
                            onChange={(e) => setNight('end', e.currentTarget.value)}
                          />
                        </td>
                        <td>
                          <select
                            aria-label={`Weekly rest pattern at ${site.ref}`}
                            value={answer.weeklyRestScheme ?? ''}
                            onChange={(e) =>
                              setSite(site.ref, {
                                weeklyRestScheme: (e.currentTarget.value || undefined) as
                                  '7-day' | '14-day' | undefined,
                              })
                            }
                          >
                            <option value="">Don't know</option>
                            <option value="7-day">A day off every week</option>
                            <option value="14-day">Two days off every fortnight</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </fieldset>
        )}
        <fieldset>
          <legend>Staff contracts</legend>
          <div class="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Started working for you</th>
                  <th scope="col">Under 18 with a contract allowing work after 10pm?</th>
                </tr>
              </thead>
              <tbody>
                {rota.employees.map((employee) => {
                  const answer = { ...employee, ...details.employees[employee.ref] };
                  return (
                    <tr key={employee.ref}>
                      <th scope="row">{employee.displayName}</th>
                      <td>
                        <input
                          type="date"
                          aria-label={`Start date for ${employee.displayName}`}
                          value={answer.employmentStartDate ?? ''}
                          onChange={(e) =>
                            setEmployee(employee.ref, {
                              employmentStartDate: e.currentTarget.value || undefined,
                            })
                          }
                        />
                      </td>
                      <td>
                        <select
                          aria-label={`Contract allows work after 10pm: ${employee.displayName}`}
                          value={toYesNo(answer.youngWorkerContractAfter22)}
                          onChange={(e) =>
                            setEmployee(employee.ref, {
                              youngWorkerContractAfter22: fromYesNo(e.currentTarget.value),
                            })
                          }
                        >
                          <option value="">Don't know or not under 18</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </fieldset>
      </details>

      <div class="actions">
        <button type="button" onClick={onBack}>
          Back to the report
        </button>
        <button type="button" onClick={() => onDone(details)}>
          Update the report
        </button>
      </div>
    </section>
  );
}
